import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api/client'
import { API_CONFIG } from '@/lib/config/api.config'
import type {
  ReqSpec, ReqSpecsResponse, CreateReqSpecRequest, UpdateReqSpecRequest,
  Requirement, RequirementsResponse, CreateRequirementRequest, UpdateRequirementRequest,
  ReqVersion, ReqVersionsResponse, CreateReqVersionRequest, UpdateReqVersionRequest,
  ReqCoverage, ReqCoverageResponse, CreateCoverageRequest,
  RequirementStats,
  DRD, DRDsResponse, CreateDRDRequest, UpdateDRDRequest,
} from '@/types/requirement.types'

const EP = API_CONFIG.ENDPOINTS

// ─── Query Keys ──────────────────────────────────────────────────────────────

export const reqSpecKeys = {
  all: ['reqSpecs'] as const,
  lists: () => [...reqSpecKeys.all, 'list'] as const,
  list: (f?: Record<string, unknown>) => [...reqSpecKeys.lists(), f] as const,
  details: () => [...reqSpecKeys.all, 'detail'] as const,
  detail: (id: number) => [...reqSpecKeys.details(), id] as const,
}

export const requirementKeys = {
  all: ['requirements'] as const,
  lists: () => [...requirementKeys.all, 'list'] as const,
  list: (f?: Record<string, unknown>) => [...requirementKeys.lists(), f] as const,
  details: () => [...requirementKeys.all, 'detail'] as const,
  detail: (id: number) => [...requirementKeys.details(), id] as const,
  versions: (id: number) => [...requirementKeys.all, 'versions', id] as const,
  coverage: (id: number) => [...requirementKeys.all, 'coverage', id] as const,
  stats: (projectId?: number) => [...requirementKeys.all, 'stats', projectId] as const,
}

export const drdKeys = {
  all: ['drds'] as const,
  lists: () => [...drdKeys.all, 'list'] as const,
  list: (f?: Record<string, unknown>) => [...drdKeys.lists(), f] as const,
  details: () => [...drdKeys.all, 'detail'] as const,
  detail: (id: number) => [...drdKeys.details(), id] as const,
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function toParams(obj?: Record<string, unknown>): string {
  if (!obj) return ''
  const p = new URLSearchParams()
  for (const [k, v] of Object.entries(obj)) {
    if (v !== undefined && v !== null && v !== '') p.append(k, String(v))
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

// ─── Req Specs ───────────────────────────────────────────────────────────────

export const useReqSpecs = (params?: {
  project_id?: number; type?: string; limit?: number; offset?: number
}) =>
  useQuery({
    queryKey: reqSpecKeys.list(params),
    queryFn: async () => {
      const { data } = await apiClient.get<ReqSpecsResponse>(
        `${EP.REQ_SPECS.LIST}${toParams(params)}`
      )
      return data.req_specs
    },
  })

export const useReqSpec = (id: number) =>
  useQuery({
    queryKey: reqSpecKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<ReqSpec>(EP.REQ_SPECS.GET(id))
      return data
    },
    enabled: !!id,
  })

export const useCreateReqSpec = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateReqSpecRequest) => {
      const { data } = await apiClient.post<ReqSpec>(EP.REQ_SPECS.CREATE, body)
      return data
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: reqSpecKeys.lists() }) },
  })
}

export const useUpdateReqSpec = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data: body }: { id: number; data: UpdateReqSpecRequest }) => {
      const { data } = await apiClient.put<ReqSpec>(EP.REQ_SPECS.UPDATE(id), body)
      return data
    },
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: reqSpecKeys.lists() })
      qc.invalidateQueries({ queryKey: reqSpecKeys.detail(v.id) })
    },
  })
}

export const useDeleteReqSpec = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(EP.REQ_SPECS.DELETE(id))
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: reqSpecKeys.lists() }) },
  })
}

// ─── Requirements ────────────────────────────────────────────────────────────

export const useRequirements = (params?: {
  srs_id?: number; project_id?: number; limit?: number; offset?: number
}) =>
  useQuery({
    queryKey: requirementKeys.list(params),
    queryFn: async () => {
      const { data } = await apiClient.get<RequirementsResponse>(
        `${EP.REQUIREMENTS.LIST}${toParams(params)}`
      )
      return data.requirements
    },
  })

export const useRequirement = (id: number) =>
  useQuery({
    queryKey: requirementKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<Requirement>(EP.REQUIREMENTS.GET(id))
      return data
    },
    enabled: !!id,
  })

export const useCreateRequirement = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: CreateRequirementRequest) => {
      const { data } = await apiClient.post<Requirement>(EP.REQUIREMENTS.CREATE, body)
      return data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: requirementKeys.lists() })
      qc.invalidateQueries({ queryKey: reqSpecKeys.lists() })
    },
  })
}

export const useUpdateRequirement = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data: body }: { id: number; data: UpdateRequirementRequest }) => {
      const { data } = await apiClient.put<Requirement>(EP.REQUIREMENTS.UPDATE(id), body)
      return data
    },
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: requirementKeys.lists() })
      qc.invalidateQueries({ queryKey: requirementKeys.detail(v.id) })
    },
  })
}

export const useDeleteRequirement = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(EP.REQUIREMENTS.DELETE(id))
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: requirementKeys.lists() })
      qc.invalidateQueries({ queryKey: reqSpecKeys.lists() })
    },
  })
}

// ─── Requirement Versions ────────────────────────────────────────────────────

export const useReqVersions = (reqId: number, active?: boolean) =>
  useQuery({
    queryKey: requirementKeys.versions(reqId),
    queryFn: async () => {
      const params = active !== undefined ? `?active=${active}` : ''
      const { data } = await apiClient.get<ReqVersionsResponse>(
        `${EP.REQUIREMENTS.VERSIONS(reqId)}${params}`
      )
      return data.versions
    },
    enabled: !!reqId,
  })

export const useCreateReqVersion = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ reqId, data: body }: { reqId: number; data: CreateReqVersionRequest }) => {
      const { data } = await apiClient.post<ReqVersion>(EP.REQUIREMENTS.VERSIONS(reqId), body)
      return data
    },
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: requirementKeys.versions(v.reqId) })
      qc.invalidateQueries({ queryKey: requirementKeys.detail(v.reqId) })
    },
  })
}

export const useUpdateReqVersion = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({
      reqId, versionId, data: body,
    }: { reqId: number; versionId: number; data: UpdateReqVersionRequest }) => {
      const { data } = await apiClient.put<ReqVersion>(
        EP.REQUIREMENTS.VERSION(reqId, versionId), body
      )
      return data
    },
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: requirementKeys.versions(v.reqId) })
      qc.invalidateQueries({ queryKey: requirementKeys.detail(v.reqId) })
    },
  })
}

// ─── Requirement Coverage ────────────────────────────────────────────────────

export const useReqCoverage = (reqId: number) =>
  useQuery({
    queryKey: requirementKeys.coverage(reqId),
    queryFn: async () => {
      const { data } = await apiClient.get<ReqCoverageResponse>(
        EP.REQUIREMENTS.COVERAGE(reqId)
      )
      return data.coverage
    },
    enabled: !!reqId,
  })

export const useCreateCoverage = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ reqId, data: body }: { reqId: number; data: CreateCoverageRequest }) => {
      const { data } = await apiClient.post<ReqCoverage>(
        EP.REQUIREMENTS.COVERAGE(reqId), body
      )
      return data
    },
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: requirementKeys.coverage(v.reqId) })
      qc.invalidateQueries({ queryKey: requirementKeys.detail(v.reqId) })
    },
  })
}

export const useDeleteCoverage = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ reqId, coverageId }: { reqId: number; coverageId: number }) => {
      await apiClient.delete(EP.REQUIREMENTS.COVERAGE_ITEM(reqId, coverageId))
    },
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: requirementKeys.coverage(v.reqId) })
      qc.invalidateQueries({ queryKey: requirementKeys.detail(v.reqId) })
    },
  })
}

// ─── Stats ───────────────────────────────────────────────────────────────────

export const useRequirementStats = (projectId?: number) =>
  useQuery({
    queryKey: requirementKeys.stats(projectId),
    queryFn: async () => {
      const params = projectId ? `?project_id=${projectId}` : ''
      const { data } = await apiClient.get<RequirementStats>(
        `${EP.REQUIREMENTS.STATS}${params}`
      )
      return data
    },
  })

// ─── Requirements Generation (async — returns 202) ──────────────────────────

export interface GenerateRequirementsRequest {
  project_id: number
  attachment_id: string
  language: string
  generate_test_cases?: boolean
  generate_test_plan?: boolean
  /** Architectural layer prefix for suite names e.g. "backend", "frontend", "llm" */
  layer?: string
}

export interface GenerateRequirementsResponse {
  status: string
  message_id: string
  message: string
}

export const useGenerateRequirements = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (body: GenerateRequirementsRequest) => {
      const { data } = await apiClient.post<GenerateRequirementsResponse>(
        EP.REQUIREMENTS.GENERATE, body
      )
      return data
    },
    onSuccess: (_, v) => {
      qc.invalidateQueries({ queryKey: requirementKeys.lists() })
      qc.invalidateQueries({ queryKey: requirementKeys.stats(v.project_id) })
    },
  })
}

// ─── DRDs (AI-Generated Design Requirements Documents) ───────────────────────

export const useDRDs = (params?: {
  project_id?: number; status?: string; limit?: number; offset?: number
}) =>
  useQuery({
    queryKey: drdKeys.list(params),
    queryFn: async () => {
      const { data } = await apiClient.get<DRDsResponse>(
        `${EP.DRDS.LIST}${toParams(params)}`
      )
      return data.drds
    },
  })

export const useDRD = (id: number) =>
  useQuery({
    queryKey: drdKeys.detail(id),
    queryFn: async () => {
      const { data } = await apiClient.get<DRD>(EP.DRDS.GET(id.toString()))
      return data
    },
    enabled: !!id,
  })

export const useDeleteDRD = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(EP.DRDS.DELETE(id.toString()))
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: drdKeys.lists() }) },
  })
}

export const useCreateDRD = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (drdData: CreateDRDRequest) => {
      const { data } = await apiClient.post<DRD>(EP.DRDS.CREATE, drdData)
      return data
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: drdKeys.lists() }) },
  })
}

export const useUpdateDRD = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateDRDRequest }) => {
      const response = await apiClient.put<DRD>(EP.DRDS.UPDATE(id.toString()), data)
      return response.data
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: drdKeys.lists() })
      qc.invalidateQueries({ queryKey: drdKeys.detail(data.id) })
    },
  })
}
